import {
  ArrowHelper,
  Box3,
  BoxGeometry,
  BufferGeometry,
  Color,
  DoubleSide,
  EdgesGeometry,
  Float32BufferAttribute,
  LineDashedMaterial,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  OrthographicCamera,
  PerspectiveCamera,
  Quaternion,
  Vector2,
  Vector3,
} from 'three';

import { LineMaterial, LineSegmentsGeometry, LineSegmentsMesh } from '@/lib/three';
import { selectionPoints } from '@/lib/three/SelectionPoints';
import type { IMeshOptions, IMeshUserData, WidgetObject, WidgetType } from '@/types';
import { ARROW_SIZE_PRESETS, ArrowSizeType } from '@/config/arrow';

import { BaseCamera } from '../camera';
import { isInImage, isNoCoordinate } from '../math';
import { getLocalValue } from '../storage';
import { AnnoInstanceItem } from './AnnoInstanceItem';
import { Label2d } from './Label2d';
import { Point3d } from './Point3d';

const _vec = new Vector3();
const _mat = new Matrix4();
const _box = new Box3();
const SCALE_SYNC_EPSILON = 1e-4;

const ARROW_SIZE_STORAGE_KEY = 'ARROW_SIZE_SETTING';

// 全局箭头大小设置，从 localStorage 读取，默认为 small
let globalArrowSize: ArrowSizeType = getLocalValue(ARROW_SIZE_STORAGE_KEY, 'small');

type ScaleSyncMode = 'keep-negative-face' | 'keep-positive-face' | 'keep-center';

const getScaleSyncMode = (scaleChange: number, localCenterDelta: number): ScaleSyncMode | null => {
  if (Math.abs(scaleChange) <= SCALE_SYNC_EPSILON) {
    return 'keep-center';
  }

  const negativeFaceDelta = localCenterDelta - scaleChange / 2;
  const positiveFaceDelta = localCenterDelta + scaleChange / 2;

  if (Math.abs(negativeFaceDelta) <= SCALE_SYNC_EPSILON) {
    return 'keep-negative-face';
  }
  if (Math.abs(positiveFaceDelta) <= SCALE_SYNC_EPSILON) {
    return 'keep-positive-face';
  }
  if (Math.abs(localCenterDelta) <= SCALE_SYNC_EPSILON) {
    return 'keep-center';
  }

  return null;
};

export class Cuboid {
  // [x, y, z, sx, sy, sz, qx, qy, qz, qw]
  static DATA_LENGTH = 10;

  /**
   * 设置全局箭头大小
   * @param size 箭头大小档位
   */
  static setGlobalArrowSize(size: ArrowSizeType) {
    globalArrowSize = size;
  }

  /**
   * 创建立体框对象，以四元数表示旋转
   * @param data [x, y, z, sx, sy, sz, qx, qy, qz, qw] three.js 坐标系
   * @param options 配置项
   * @returns
   */
  static create(data: WidgetObject['data'], options: IMeshOptions): Mesh<BufferGeometry, MeshBasicMaterial> {
    const direction: [number, number, number] = [1, 0, 0];
    const { color: colorHex, opacity: faceOpacity, anchor, canvasSize = [1000, 1000], point_cnt } = options;

    // 此处设为(1, 1, 1)，方便通过 .scale 更新大小，确保编辑时边框也同步跟随 scale 缩放
    const geometry = new BoxGeometry(1, 1, 1).toNonIndexed();
    const material = new MeshBasicMaterial({
      vertexColors: true,
      transparent: true,
      side: DoubleSide,
    });

    const colors = [];
    const color = new Color(colorHex);
    for (let i = 0; i < geometry.attributes.position.array.length; i += 3) {
      colors.push(color.r, color.g, color.b, faceOpacity);
      colors.push(color.r, color.g, color.b, faceOpacity);
      colors.push(color.r, color.g, color.b, faceOpacity);
    }
    geometry.setAttribute('color', new Float32BufferAttribute(colors, 4));

    const cubeAnchor: [number, number, number] | undefined = anchor
      ? Array.isArray(anchor)
        ? anchor
        : [0, 0, 0.52]
      : undefined;
    const cuboid = new Mesh(geometry, material);
    cuboid.name = cuboid.uuid;
    cuboid.userData = {
      type: 'cuboid',
      color: colorHex,
      opacity: faceOpacity,
      direction,
      anchor: cubeAnchor,
      point_cnt,
    } satisfies IMeshUserData;

    const borderGeometry = new LineSegmentsGeometry();
    borderGeometry.fromEdgesGeometry(new EdgesGeometry(geometry));
    const border = new LineSegmentsMesh(
      borderGeometry,
      new LineMaterial({
        linewidth: 1,
        color: colorHex,
        resolution: new Vector2(...canvasSize),
      })
    );
    cuboid.add(border);

    if (cubeAnchor) {
      const label = Label2d.create(cuboid.name, cubeAnchor);
      cuboid.add(label);
      // 添加删除事件监听，以便移除标签 dom 节点
      cuboid.addEventListener('removed', () => {
        cuboid.remove(label);
      });
    }

    Cuboid.update(cuboid, data);
    Cuboid.setDirection(cuboid, direction, globalArrowSize);

    return cuboid;
  }

  /**
   * 根据 6 个关键点和指定的旋转角度创建贴合的最小立体框
   * @param points
   * @param quaternion
   * @param options
   * @returns
   */
  static createFromPoints(points: Vector3[], quaternion: Quaternion, options: IMeshOptions) {
    const { position, scale } = Cuboid.getMinCubeFromPoints(points, quaternion);
    return Cuboid.create(position.toArray().concat(scale.toArray()).concat(quaternion.toArray()), options);
  }

  /**
   * 更新立体框
   * @param target 更新目标
   * @param data [x, y, z, sx, sy, sz, qx, qy, qz, qw] three.js 坐标系
   */
  static update(target: Mesh<BufferGeometry, MeshBasicMaterial>, data: WidgetObject['data']) {
    if (data.length === Cuboid.DATA_LENGTH) {
      target.position.fromArray(data, 0);
      target.scale.fromArray(data, 3);
      target.quaternion.fromArray(data, 6);
    }
  }

  /**
   * 根据 widget 生成立体框对象
   * @param name 唯一ID
   * @param widget [x, y, z, sx, sy, sz, qx, qy, qz, qw] 文件坐标系
   * @param matrix 从点云文件坐标系 到 three.js 坐标系的转换矩阵
   * @param options
   * @returns
   */
  static createFromWidget(
    name: string,
    widget: WidgetObject,
    matrix: Matrix4,
    options: IMeshOptions
  ): Mesh<BufferGeometry, MeshBasicMaterial> | null {
    const transformedWidget = Cuboid.getTransformedWidget(widget, matrix);
    if (!transformedWidget) return null;
    const { data, direction } = transformedWidget;

    const cuboid = Cuboid.create(data, {
      ...options,
      direction,
      point_cnt: widget.point_cnt,
    });
    cuboid.name = name;

    if (cuboid.userData.anchor) {
      // 如果有标签锚点，则遍历子对象，尝试更新标签元素id
      cuboid.children.forEach((child) => {
        Label2d.updateName(child, name);
      });
    }

    return cuboid;
  }

  /**
   * 由 AnnoInstanceItem.widget 数据更新立体框
   * @param target 更新目标
   * @param widget [x, y, z, sx, sy, sz, qx, qy, qz, qw] 文件坐标系
   * @param matrix 从点云文件坐标系 到 three.js 坐标系的转换矩阵
   */
  static updateFromWidget(target: Mesh<BufferGeometry, MeshBasicMaterial>, widget: WidgetObject, matrix: Matrix4) {
    const transformedWidget = Cuboid.getTransformedWidget(widget, matrix);
    if (!transformedWidget) return;
    const { data } = transformedWidget;
    data && Cuboid.update(target, data);
  }

  /**
   * 导出Widget格式数据
   * @param target
   * @param matrix 从 threejs 到文件坐标系
   * @returns
   */
  static transOutWidget(
    target: Mesh<BufferGeometry, MeshBasicMaterial>,
    matrix: Matrix4,
    camera?: PerspectiveCamera | OrthographicCamera,
    pointCloud?: Object3D
  ): WidgetObject {
    const {
      position,
      quaternion,
      scale,
      userData: { direction, point_cnt: initPointsCount },
    } = target;

    const _position = new Vector3();
    const _scale = new Vector3();
    const _quater = new Quaternion();

    _mat.compose(position, quaternion, scale).premultiply(matrix).decompose(_position, _quater, _scale);

    const toward = new Vector3(...(direction as [number, number, number])).applyQuaternion(_quater).add(_position);
    const pointsCount = camera && pointCloud ? this.updatePointCount(target, camera, pointCloud) : initPointsCount;
    return {
      name: 'cuboid',
      data: _position.toArray().concat(_scale.toArray()).concat(_quater.toArray()),
      forward: {
        origin: _position.toArray(),
        toward: toward.toArray(),
      },
      direction,
      point_cnt: pointsCount,
    };
  }

  /**
   * 矩阵变换 AnnoInstanceItem.widget 数据
   * @param wdata [x, y, z, sx, sy, sz, qx, qy, qz, qw] 文件坐标系
   * @param matrix 从点云文件坐标系 到 three.js 坐标系的转换矩阵
   * @returns
   */
  static getComposeTransformData(wdata: number[], matrix: Matrix4) {
    const position = new Vector3().fromArray(wdata, 0);
    const scale = new Vector3().fromArray(wdata, 3);
    const quaternion = new Quaternion().fromArray(wdata, 6);

    _mat.compose(position, quaternion, scale).premultiply(matrix).decompose(position, quaternion, scale);

    const data = position.toArray().concat(scale.toArray()).concat(quaternion.toArray());

    return {
      data: isNoCoordinate(data) ? [] : data,
      position,
      scale,
      quaternion,
    };
  }

  /**
   * 把 widget 的数据根据传入的 matrix 转成符合结果的数据
   * @param widget [x, y, z, sx, sy, sz, qx, qy, qz, qw] 文件坐标系
   * @param matrix 转换需要的矩阵
   * @returns
   */
  static getTransformedWidget(widget: WidgetObject, matrix: Matrix4) {
    const { data: wdata, forward, direction: dir } = widget;

    const { data, quaternion } = Cuboid.getComposeTransformData(wdata, matrix);

    if (!data?.length) return null;

    const direction = dir ?? Cuboid.formatDirection(forward, quaternion);

    return {
      ...widget,
      data,
      direction,
    };
  }

  /**
   * 获取立方体的8个顶点
   * @param position 立方体中心点位置
   * @param quaternion 旋转四元数
   * @param scale 立方体尺寸
   * @returns
   */
  static getVertexs(position: Vector3, quaternion: Quaternion, scale: Vector3) {
    const size = new Vector3(1, 1, 1);
    const vertexs = [
      new Vector3(size.x / 2, size.y / 2, size.z / 2),
      new Vector3(size.x / 2, -size.y / 2, size.z / 2),
      new Vector3(-size.x / 2, -size.y / 2, size.z / 2),
      new Vector3(-size.x / 2, size.y / 2, size.z / 2),

      new Vector3(size.x / 2, size.y / 2, -size.z / 2),
      new Vector3(size.x / 2, -size.y / 2, -size.z / 2),
      new Vector3(-size.x / 2, -size.y / 2, -size.z / 2),
      new Vector3(-size.x / 2, size.y / 2, -size.z / 2),
    ];

    const transMatrix = new Matrix4().compose(position, quaternion, scale);
    return vertexs.map((v) => v.applyMatrix4(transMatrix));
  }

  /**
   * 获取立方体的顶点坐标数组
   * @param widgetData
   * @returns
   */
  static getVertexFromWidget(widgetData: number[]) {
    const position = new Vector3().fromArray(widgetData, 0);
    const scale = new Vector3().fromArray(widgetData, 3);
    const quaternion = new Quaternion().fromArray(widgetData, 6);
    const vertexs = Cuboid.getVertexs(position, quaternion, scale);

    return vertexs.reduce((prev, vec) => prev.concat(vec.toArray()), [] as number[]);
  }

  static getMinCubeFromPoints(points: Vector3[], quaternion: Quaternion) {
    // 先把 points 按反方向旋转，得到 box，再把 box 应用同样的旋转
    const inverseQuaternion = new Quaternion().copy(quaternion).invert();

    for (let point of points) {
      point.applyQuaternion(inverseQuaternion);
    }

    _box.setFromPoints(points);
    const position = _box.getCenter(new Vector3());
    position.applyQuaternion(quaternion);
    for (let point of points) {
      point.applyQuaternion(quaternion);
    }

    const scale = _box.getSize(new Vector3());
    return { position, scale };
  }

  static formatDirection(forward: WidgetObject['forward'], rotation: Quaternion): [number, number, number] {
    let direction: [number, number, number] = [1, 0, 0];
    // 由 forward 数据计算 direction
    if (forward) {
      // 将 forward 格式化为 Vector3 并归一化
      const { origin = [0, 0, 0], toward } = forward;
      const [ori, tow] = [origin, toward].map((val) => {
        const vec = new Vector3();
        val.forEach((v, i) => vec.setComponent(i, v));
        return vec;
      });
      const forwardDir = tow.sub(ori).normalize();

      // 枚举所有可能的方向
      const mayDirs: [number, number, number][] = [
        [1, 0, 0],
        [-1, 0, 0],
        [0, 1, 0],
        [0, -1, 0],
        [0, 0, 1],
        [0, 0, -1],
      ];

      let minAngle = Number.MAX_SAFE_INTEGER;
      let minIndex = 0;
      let targetDir = new Vector3();

      // 判断与 forwardDir 向量距离最小的单位向量
      mayDirs.forEach((dir, i) => {
        const angle = targetDir
          .set(...dir)
          .applyQuaternion(rotation)
          .angleTo(forwardDir);
        if (angle < minAngle) {
          minIndex = i;
          minAngle = angle;
        }
      });
      direction = mayDirs[minIndex];
    }
    return direction;
  }

  static updatePointCount(
    target: Mesh<BufferGeometry, MeshBasicMaterial>,
    camera: PerspectiveCamera | OrthographicCamera,
    pointCloud: Object3D
  ) {
    const { position, scale, quaternion } = target;
    selectionPoints.updateCameraAndPointCloud(camera, pointCloud);
    const pointsPositions = selectionPoints.searchChildInVertexs(Cuboid.getVertexs(position, quaternion, scale));
    // pointsPositions 为 [x, y, z, x2, y2, z2, ...]，因此除以3
    const pointsCount = pointsPositions.length / 3;
    target.userData.point_cnt = pointsCount;
    return pointsCount;
  }

  /**
   * 设置立方体方向
   * @param vec 面的法向量，为单位向量，且与坐标轴平行
   * @param arrowSize 箭头大小档位，默认为 'small'
   */
  static setDirection(
    target: Mesh<BufferGeometry, MeshBasicMaterial>,
    vec: [number, number, number],
    arrowSize: ArrowSizeType = 'small'
  ) {
    // 判断输入向量是否包含 1 或 -1，且在包含的情况下忽略其它值
    const index = vec.findIndex((val) => val === 1 || val === -1);
    if (index < 0) {
      return;
    }

    // 格式化方向向量
    const newDir: [number, number, number] = [0, 0, 0];
    newDir[index] = vec[index];

    const { color, opacity: faceOpacity, direction: prevDirection } = target.userData;
    // 遍历子对象查找箭头
    const mesh = target.children.find((mesh) => mesh instanceof ArrowHelper) as ArrowHelper | undefined;

    // 获取箭头配置
    const arrowConfig = ARROW_SIZE_PRESETS[arrowSize];

    // 如果选择"不显示"，移除现有箭头
    if (!arrowConfig) {
      if (mesh) {
        target.remove(mesh);
      }
      Cuboid.setFaceOpacity(target, newDir, faceOpacity + 0.2);
      target.userData.direction = newDir;
      return;
    }

    // 检查方向是否改变
    const directionChanged = !prevDirection || !newDir.reduce((prev, cur, i) => prev && cur === prevDirection?.[i], true);

    if (directionChanged && prevDirection) {
      // 将当前面恢复透明度
      Cuboid.setFaceOpacity(target, prevDirection, faceOpacity);
    }

    if (mesh) {
      // 更新现有箭头
      if (directionChanged) {
        mesh.setDirection(new Vector3(...newDir));
      }
      // 总是更新箭头大小（即使方向没变）
      mesh.setLength(arrowConfig.length, arrowConfig.headLength, arrowConfig.headWidth);
      // 更新虚线材质
      if (mesh.line.material instanceof LineDashedMaterial) {
        mesh.line.material.dashSize = arrowConfig.dashSize;
        mesh.line.material.gapSize = arrowConfig.gapSize;
        mesh.line.computeLineDistances();
      }
    } else {
      // 创建新箭头
      const arrowHelper = new ArrowHelper(
        new Vector3(...newDir),
        new Vector3(0, 0, 0),
        arrowConfig.length,
        color,
        arrowConfig.headLength,
        arrowConfig.headWidth
      );
      arrowHelper.line.material = new LineDashedMaterial({
        color,
        dashSize: arrowConfig.dashSize,
        gapSize: arrowConfig.gapSize,
      });
      arrowHelper.line.computeLineDistances();
      target.add(arrowHelper);
    }
    Cuboid.setFaceOpacity(target, newDir, faceOpacity + 0.2);
    target.userData.direction = newDir;
  }

  static changeColor(target: Mesh<BufferGeometry, MeshBasicMaterial>, colorHex: string, refreshData = true) {
    const colors = [];
    const color = new Color(colorHex);
    const opacity = target.userData.opacity;
    // 更改立方体的颜色
    const geometry = target.geometry;
    for (let i = 0; i < geometry.attributes.position.array.length; i += 3) {
      colors.push(color.r, color.g, color.b, opacity);
      colors.push(color.r, color.g, color.b, opacity);
      colors.push(color.r, color.g, color.b, opacity);
    }
    geometry.setAttribute('color', new Float32BufferAttribute(colors, 4));

    // 更改边框和箭头的颜色
    for (let i = 0, l = target.children.length; i < l; i++) {
      const obj = target.children[i];
      if (obj instanceof LineSegmentsMesh && 'color' in obj.material) {
        obj.material.color.set(color);
      } else if (obj instanceof ArrowHelper) {
        obj.setColor(color);
      }
    }

    if (refreshData) {
      target.userData.color = colorHex;
    }
    Cuboid.setFaceOpacity(target, target.userData.direction, opacity + 0.2);
  }

  /**
   * 设置立方体的某个面的透明度
   * @param vec 面的法向量，由立方体中心点指向面中心点
   * @param opacity 透明度
   * @returns
   */
  static setFaceOpacity(
    target: Mesh<BufferGeometry, MeshBasicMaterial>,
    vec: [number, number, number],
    opacity: number
  ) {
    // 判断输入向量是否包含 1 或 -1，且在包含的情况下忽略其它值
    const axisId = vec.findIndex((val) => val === 1 || val === -1);
    if (axisId < 0) {
      return;
    }

    // 获取坐标位置值 0.5 / -0.5
    const cursor = vec[axisId] / 2;
    const positions = target.geometry.attributes.position.array;
    const colorAttribute = target.geometry.getAttribute('color');

    let id: number;

    // 遍历几何体的面，查找对应法向量的面，并设置其材质的opacity
    for (let i = 0; i < positions.length; i += 9) {
      // 每个面有三个顶点，每个顶点有3个位置元素(x, y, z)，因此循环步长为9
      const wPositions = [positions[i + axisId], positions[i + axisId + 3], positions[i + axisId + 6]]; // 获取一个面的三个顶点的值

      if (wPositions[0] === cursor && wPositions[1] === cursor && wPositions[2] === cursor) {
        // 如果三个顶点的单轴值都等于cursor，则认为当前三个点构成的三角面片属于法向量对应的面
        id = i / 3;
        colorAttribute.setW(id, opacity);
        colorAttribute.setW(id + 1, opacity);
        colorAttribute.setW(id + 2, opacity);
      }
    }
    colorAttribute.needsUpdate = true;
  }

  /**
   * 从关键帧中得到当前 index 的标注物
   * @param beginItem 之前的关键帧
   * @param endItem 之后的关键帧
   * @param index 在 count 只中是第几个，从 1 开始，最大是 count
   * @param count 关键帧中间的插值个数
   * @returns
   */
  static getInterpolatedCuboidWidget(
    beginItemWidget: AnnoInstanceItem['widget'],
    endItemWidget: AnnoInstanceItem['widget'] | null = null,
    index: number,
    count: number
  ) {
    const beginData = beginItemWidget.data;
    if (!beginData) return null;
    if (!endItemWidget) return { ...beginItemWidget };
    const endData = endItemWidget.data;
    // [x, y, z, sx, sy, sz, qx, qy, qz, qw]
    const positionOffset = 3;
    const quaterOffset = Cuboid.DATA_LENGTH - 4;
    // 默认生成的是跟 beginItem 同样位置和大小的标注物
    const increateSteps = Array(quaterOffset + 1).fill(0);

    if (endItemWidget) {
      for (let i = 0; i < increateSteps.length; i++) {
        if (i >= quaterOffset) {
          // 四元数差值因子 t
          increateSteps[i] = 1 / (count + 1);
        } else if (i < positionOffset) {
          increateSteps[i] = (endData[i] - beginData[i]) / (count + 1);
        }
      }
    }
    const widgetData: number[] = Array(Cuboid.DATA_LENGTH).fill(0);

    for (let j = 0; j < increateSteps.length; j++) {
      if (j < quaterOffset) {
        widgetData[j] = beginData[j] + increateSteps[j] * index;
      } else {
        Quaternion.slerpFlat(
          widgetData,
          quaterOffset,
          beginData,
          quaterOffset,
          endData,
          quaterOffset,
          increateSteps[j] * index
        );
      }
    }

    const direction = Cuboid.getDirectionFromWidget({
      ...beginItemWidget,
      data: widgetData,
    });
    return {
      name: 'cuboid' as WidgetType,
      data: widgetData,
      direction,
    };
  }

  /**
   * 把 target 的位置和大小变换成 source 的位置和大小
   * @param source 源标注物
   * @param target 目标标注物
   */
  static changeToSameScaleAndDirection(source: AnnoInstanceItem, target: AnnoInstanceItem, oldSourceWidget?: WidgetObject) {
    const sourceScale = source.widget.data.slice(3, 6),
      targetData = target.widget.data;

    const keepTargetCenter = () => {
      target.widget.data = targetData.slice(0, 3).concat(sourceScale, targetData.slice(6));
    };

    if (!oldSourceWidget) {
      keepTargetCenter();
      target.widget.direction = source.widget.direction;
      return;
    }

    const oldSourceData = oldSourceWidget.data;
    const newSourceData = source.widget.data;
    const oldSourceQuaternion = new Quaternion().fromArray(oldSourceData, 6).normalize();
    const newSourceQuaternion = new Quaternion().fromArray(newSourceData, 6).normalize();

    if (1 - Math.abs(oldSourceQuaternion.dot(newSourceQuaternion)) > SCALE_SYNC_EPSILON) {
      keepTargetCenter();
      target.widget.direction = source.widget.direction;
      return;
    }

    const localCenterDelta = new Vector3()
      .fromArray(newSourceData, 0)
      .sub(new Vector3().fromArray(oldSourceData, 0))
      .applyQuaternion(oldSourceQuaternion.clone().invert());

    const targetCenterOffset = [0, 0, 0];
    const oldSourceScale = oldSourceData.slice(3, 6);
    const targetScale = targetData.slice(3, 6);

    for (let i = 0; i < 3; i++) {
      const scaleChange = sourceScale[i] - oldSourceScale[i];
      const targetScaleChange = sourceScale[i] - targetScale[i];
      const syncMode = getScaleSyncMode(scaleChange, localCenterDelta.getComponent(i));

      if (syncMode === 'keep-negative-face') {
        targetCenterOffset[i] = targetScaleChange / 2;
      } else if (syncMode === 'keep-positive-face') {
        targetCenterOffset[i] = -targetScaleChange / 2;
      } else if (syncMode === null) {
        targetCenterOffset[i] = 0;
      }
    }

    const targetPosition = new Vector3()
      .fromArray(targetData, 0)
      .add(new Vector3().fromArray(targetCenterOffset).applyQuaternion(new Quaternion().fromArray(targetData, 6)));

    target.widget.data = targetPosition.toArray().concat(sourceScale, targetData.slice(6));
    target.widget.direction = source.widget.direction;
  }

  /**
   * 从标注物数据得到 direction
   * @param annoItem 标注物数据
   * @returns
   */
  static getDirectionFromWidget(widget: WidgetObject) {
    const { data: wdata, forward, direction: annoDirection } = widget;
    const direction =
      annoDirection ?? (forward ? Cuboid.formatDirection(forward, new Quaternion().fromArray(wdata, 6)) : [1, 0, 0]);
    return direction;
  }

  /**
   * 标准映射方法
   * @param data 立方体的8个顶点坐标值数组，顶点顺序不限 [x1, y1, z1, x2, y2, z2, ...]
   * @param matrix
   * @param cameraModel
   * @param imageSize
   * @returns 矩形框数据 [centerX, centerY, width, height]
   */
  static getFusionMapped(
    data: number[],
    matrix: Matrix4,
    cameraModel: BaseCamera,
    imageSize?: { width: number; height: number }
  ): WidgetObject | undefined {
    if (!imageSize || data.length < 24) return undefined;

    // 矩形点坐标：左上点，右下点
    const rect = [Number.MAX_SAFE_INTEGER, Number.MAX_SAFE_INTEGER, Number.MIN_SAFE_INTEGER, Number.MIN_SAFE_INTEGER];

    for (let i = 0; i < data.length; i += 3) {
      const mappedPoint = Point3d.getVectorFusionMapped(_vec.fromArray(data, i), matrix, cameraModel);

      if (mappedPoint) {
        rect[0] = Math.min(rect[0], mappedPoint.x);
        rect[1] = Math.min(rect[1], mappedPoint.y);
        rect[2] = Math.max(rect[2], mappedPoint.x);
        rect[3] = Math.max(rect[3], mappedPoint.y);
      }
    }

    const { width = 0, height = 0 } = imageSize;

    // 判断矩形生成条件：矩形尺寸必须大于 1 * 1，且至少有一个点在图片范围内
    if (
      rect[0] + 1 < rect[2] &&
      rect[1] + 1 < rect[3] &&
      width > 0 &&
      height > 0 &&
      [rect.slice(0, 2), rect.slice(2)].some(isInImage(width, height))
    ) {
      return {
        name: 'box2d',
        data: [(rect[0] + rect[2]) / 2, (rect[1] + rect[3]) / 2, rect[2] - rect[0], rect[3] - rect[1]],
      };
    }

    return undefined;
  }
}
